import {
    AfterViewInit, Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild,
} from '@angular/core';
import {
    AbstractControl, FormControl, ValidationErrors, Validators,
} from '@angular/forms';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { filter, finalize, takeUntil } from 'rxjs/operators';
import { ReportProblemService } from 'src/app/global/services/report-problem/report-problem.abstract.service';
import { ConfirmDialogV2Component } from '../confirm-dialog-v2/confirm-dialog-v2.component';
import { ReportAttachmentsComponent } from './report-attachments/report-attachments.component';
import {
    CreateProblemReportRequest, ReportAttachment, ReportModule, ReportSnapshot,
} from './report-problem.model';

export const REPORT_PROBLEM_DIALOG_ID = 'report-problem-dialog';
export const DESCRIPTION_MAX_LENGTH = 5000;

export interface ReportProblemDialogData {
    snapshot: ReportSnapshot;
}

/** Valid as soon as there is some text: no arbitrary minimum length. */
export function notBlankValidator(control: AbstractControl): ValidationErrors | null {
    return typeof control.value === 'string' && control.value.trim().length > 0 ? null : { blank: true };
}

@Component({
    selector: 'app-report-problem-dialog',
    templateUrl: './report-problem-dialog.component.html',
    styleUrls: ['./report-problem-dialog.component.scss'],
})
export class ReportProblemDialogComponent implements OnInit, AfterViewInit, OnDestroy {
    @ViewChild(ReportAttachmentsComponent) attachmentsComponent: ReportAttachmentsComponent;

    @ViewChild('closeButton') closeButton: ElementRef<HTMLButtonElement>;

    readonly snapshot: ReportSnapshot;

    description = new FormControl('', [notBlankValidator, Validators.maxLength(DESCRIPTION_MAX_LENGTH)]);

    blocking = new FormControl(false);

    selectedModuleKey: string;

    editingModule = false;

    attachments: ReportAttachment[] = [];

    attachmentsUploading = false;

    dragOver = false;

    submitting = false;

    submitError = false;

    createdKey: string = null;

    private destroy$ = new Subject<void>();

    constructor(
        public dialogRef: MatDialogRef<ReportProblemDialogComponent>,
        @Inject(MAT_DIALOG_DATA) data: ReportProblemDialogData,
        private reportProblemService: ReportProblemService,
        private dialog: MatDialog,
        private translate: TranslateService,
        private elementRef: ElementRef<HTMLElement>,
    ) {
        this.snapshot = data.snapshot;
        this.selectedModuleKey = data.snapshot.detectedModule.key;
    }

    get selectedModule(): ReportModule {
        return this.snapshot.modules.find((module) => module.key === this.selectedModuleKey) ?? this.snapshot.detectedModule;
    }

    get canSubmit(): boolean {
        return this.description.valid && !this.attachmentsUploading && !this.submitting;
    }

    get isDirty(): boolean {
        return (
            (this.description.value ?? '').trim().length > 0
            || this.attachments.length > 0
            || this.attachmentsUploading
            || this.blocking.value === true
            || this.selectedModuleKey !== this.snapshot.detectedModule.key
        );
    }

    ngOnInit(): void {
        this.dialogRef
            .keydownEvents()
            .pipe(
                filter((event) => event.key === 'Escape'),
                takeUntil(this.destroy$),
            )
            .subscribe(() => this.requestClose());
    }

    ngAfterViewInit(): void {
        setTimeout(() => this.elementRef.nativeElement.querySelector('textarea')?.focus());
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    selectModule(key: string): void {
        this.selectedModuleKey = key;
        this.editingModule = false;
    }

    onAttachmentsChanged(attachments: ReportAttachment[]): void {
        this.attachments = attachments;
    }

    onPaste(event: ClipboardEvent): void {
        if (!this.submitting) {
            this.attachmentsComponent?.handlePaste(event);
        }
    }

    onDrop(event: DragEvent): void {
        event.preventDefault();
        if (!this.submitting && !this.createdKey) {
            this.attachmentsComponent?.onDrop(event);
        }
    }

    /** The only payload builder: what the details block shows is exactly what is sent. */
    buildPayload(): CreateProblemReportRequest {
        return {
            description: (this.description.value ?? '').trim(),
            blocking: this.blocking.value === true,
            module: { ...this.selectedModule, detectedKey: this.snapshot.detectedModule.key },
            attachments: this.attachments,
            context: this.snapshot.context,
        };
    }

    submit(): void {
        if (!this.canSubmit) return;

        this.submitting = true;
        this.submitError = false;
        this.description.disable({ emitEvent: false });
        this.blocking.disable({ emitEvent: false });

        this.reportProblemService
            .create(this.buildPayload())
            .pipe(
                finalize(() => {
                    this.submitting = false;
                    this.description.enable({ emitEvent: false });
                    this.blocking.enable({ emitEvent: false });
                }),
                takeUntil(this.destroy$),
            )
            .subscribe({
                next: ({ key }) => {
                    this.createdKey = key;
                    setTimeout(() => this.closeButton?.nativeElement.focus());
                },
                error: () => {
                    this.submitError = true;
                },
            });
    }

    requestClose(): void {
        if (this.submitting) return;

        if (this.createdKey || !this.isDirty) {
            this.dialogRef.close(this.createdKey);
            return;
        }

        const confirmDialogRef = this.dialog.open(ConfirmDialogV2Component, {
            autoFocus: false,
            panelClass: 'bciti-confirmation-dialog',
            data: {
                title: this.translate.instant('components.report-problem-dialog.discard.title'),
                body: this.translate.instant('components.report-problem-dialog.discard.body'),
                buttons: {
                    cancel: { text: this.translate.instant('components.report-problem-dialog.discard.cancel'), class: 'bciti-button bciti-button--black-inversed' },
                    confirm: { text: this.translate.instant('components.report-problem-dialog.discard.confirm'), class: 'bciti-button bciti-button--blue' },
                },
            },
        });

        confirmDialogRef.componentInstance.onSubmit.pipe(takeUntil(this.destroy$)).subscribe((confirmed: boolean) => {
            confirmDialogRef.close();
            if (confirmed) {
                this.dialogRef.close();
            }
        });
    }
}
